import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { triggerAnalyticsEvent } from 'src/app/utils/analytics/trigger-analytics';
import {
  UiAccountsControllerService,
  UiOneTimeWebhookControllerService,
} from 'src/app/cc-api/services';
import { toTitleCase } from 'src/app/utils/string-util';
import { FacetsPage } from 'src/app/facets/facets-page/facets-page.component';
import { NbPopoverDirective } from '@nebular/theme';
import {
  protocolDisallowPattern,
  whiteSpaceInputPattern,
} from 'src/app/constants/inputPatterns';
import { Subscription, interval, switchMap, startWith } from 'rxjs';
import { vcsAccountIcons } from 'src/app/components/create-vcs-account-integration/accounts-constants';
import { TokenExpiryService } from 'src/app/services/token-expiry.service';
import debounce from 'lodash/debounce';
import { VCSLinkingRequest } from 'src/app/cc-api/models/vcslinking-request';
import { CustomToastService } from 'src/app/pages/custom-toast/custom-toast.service';

@Component({
  selector: 'app-create-vcs-app-integration-v3',
  templateUrl: './create-vcs-app-integration-v3.component.html',
  styleUrls: ['./create-vcs-app-integration-v3.component.scss'],
})
export class CreateVcsAppV3Component extends FacetsPage {
  @Output() afterSaveAccount: EventEmitter<any> = new EventEmitter<any>();
  @Output() afterChangeIntegration: EventEmitter<any> = new EventEmitter<any>();
  @ViewChild(NbPopoverDirective) popover: NbPopoverDirective; //copy popover
  @Input() passedRef: any;
  @Input() vcsAccountType: any;
  @Input() allowCancel: boolean = true;
  vcsAccountIcons = vcsAccountIcons;
  accountForm: UntypedFormGroup = this.fb.group({
    accountName: [
      '',
      [Validators.required, Validators.pattern('^[a-z0-9-]+$')],
    ],
    hostNameToggle: [false],
    hostedOn: ['github.com'],
  });
  orgNameForm: FormGroup = this.fb.group({
    selectedOrgName: ['', [Validators.required]],
    projectId: ['', [Validators.required]],
  });
  readonly ACCOUNTS_WRITE = 'ACCOUNTS_WRITE';
  alertTitle: string;
  alertType: string;
  alertDescription: string;
  creatingVcsAccount: boolean = false;
  accountCreateError: boolean = false;
  enableStepTwo: boolean = false;
  pollingWebhook: boolean = false;
  loadingOrgNames: boolean = false;
  pollingInterval: number = 5000;
  orgNames: { label: string; value: string }[] = [];
  webhookSubscription: Subscription;
  webhookCreating: boolean = false;
  projectKeyOptions: { label: string; value: string }[] = [];
  updatingVcsAccount: boolean = false;
  account: any;
  orgsLinkedToAccount: boolean = false;
  accountOrgUpdateError: boolean = false;
  loadingProjectKey: boolean = false;
  parentWindow: any;
  organizationKeyWordMap = {
    BITBUCKET: 'Workspaces',
    GITLAB: 'Groups',
  };

  constructor(
    private activatedRoute: ActivatedRoute,
    private accountControllerService: UiAccountsControllerService,
    private fb: FormBuilder,
    private tokenExpiryService: TokenExpiryService,
    private webhookIntegrationService: UiOneTimeWebhookControllerService,
    private router: Router,
    private customToast: CustomToastService,
  ) {
    super(activatedRoute);
  }

  get accountName() {
    return this.accountForm.get('accountName');
  }

  get hostedOn() {
    return this.accountForm.get('hostedOn');
  }

  get selectedOrgName() {
    return this.orgNameForm.get('selectedOrgName');
  }

  get projectId() {
    return this.orgNameForm.get('projectId');
  }

  get hostNameToggle() {
    return this.accountForm.get('hostNameToggle');
  }

  //check if github account is created and there is no error while creating the account.
  //if no error then show Done button to the user which will close the popup
  get githubAccountCreated() {
    return !this.accountCreateError && this.vcsAccountType === 'GITHUB';
  }

  //check if gitlab and bitbucket account is updated with workspace name and show the final done button and there should is no error while updating the account.
  //if no error then show Done button to the user which will close the popup
  get nonGithubAccountUpdated() {
    return (
      !this.accountOrgUpdateError &&
      this.orgsLinkedToAccount &&
      ['GITLAB', 'BITBUCKET'].includes(this.vcsAccountType)
    );
  }

  OnPageLoad(p: Params): void {
    this.alertDescription = '';
    this.account = null;
    this.accountOrgUpdateError = false;
    this.enableStepTwo = false;
    this.accountCreateError = false;
    if (this.vcsAccountType === 'GITHUB') {
      this.hostedOn.addValidators([
        Validators.required,
        Validators.pattern(protocolDisallowPattern),
      ]);
    }
    if (this.vcsAccountType === 'GITLAB') {
      this.projectId?.clearValidators();
      this.projectId?.updateValueAndValidity();
      this.hostedOn.setValue('https://gitlab.com');
    }
    this.parentWindow = window;
  }

  /*
      * Clicking on Next button will have 2 conditions.
      1. When step 2 is not shown to the user, then show step 2 with token generation instruction and input field
      2. When step 2 is shown to the user, submit the account and show success or error message to the user
  
      * While saving the account input fields needs to be disabled and they should stay disabled when success message is shown
    */
  showNextStep() {
    if (!this.enableStepTwo) {
      this.enableStepTwo = true;
      this.accountName?.disable();
      this.hostedOn.disable();
      this.webhookCreating = true;
      triggerAnalyticsEvent(`ORG_SETTINGS_ACCOUNT_MANAGEMENT_ADDITION_LINK`, {
        accountName: this.accountName?.value,
        provider: this.vcsAccountType,
      });
      const linkApiParams: VCSLinkingRequest = {
        accountName: this.accountName?.value,
        enterpriseHostName: ['github.com', 'https://gitlab.com'].includes(
          this.hostedOn?.value?.trim(),
        )
          ? null
          : this.hostedOn?.value?.trim(),
        webhook: {
          status: 'WAITING',
          type: this.vcsAccountType,
        },
      };
      this.accountControllerService
        .requestVCSLinkingUsingPOST(linkApiParams)
        .subscribe(
          (webhookResp) => {
            this.webhookCreating = false;
            if (webhookResp?.redirectUrl) {
              this.openChildWindow(webhookResp?.redirectUrl);
              this.closeAndReturnToParent();
            }
            this.startPollingWebhook(webhookResp?.webhook?.id);
          },
          (error) => {
            this.webhookCreating = false;
            if (error?.error?.code === '400') {
              this.enableStepTwo = false;
            }
            this.accountName.enable();
            this.hostedOn.enable();
            this.customToast.error(
              error?.error?.message || `Account linking failed`,
            );
          },
        );
    }
  }

  openChildWindow(url) {
    // Store reference to parent window
    const parentWindow = window;

    // Open child window and store its reference
    const childWindow = window?.open(url, '_blank');

    // Add event listener for when child window closes
    const checkChild = setInterval(() => {
      if (childWindow?.closed) {
        clearInterval(checkChild);
        // Focus back to parent window
        parentWindow?.focus();
      }
    }, 500);

    return childWindow;
  }

  // Child window code
  closeAndReturnToParent() {
    // Optional: You can communicate with parent window if needed
    if (window?.opener && !window?.opener?.closed) {
      window?.opener?.focus();
    }

    // Close the current window
    window.close();
  }

  startPollingWebhook(webhookId) {
    this.pollingWebhook = true;
    this.enableStepTwo = true;
    this.webhookSubscription = interval(this.pollingInterval)
      .pipe(
        startWith(0),
        switchMap(() =>
          this.webhookIntegrationService.pollWebhookUsingGET(webhookId),
        ),
      )
      .subscribe(
        async (response: any) => {
          if (response && response?.status !== 'WAITING') {
            this.pollingWebhook = false;
            this.alertType =
              response?.status === 'SUCCESS' ? 'success' : 'danger';
            if (response?.status === 'SUCCESS') {
              this.accountControllerService
                ?.getAccountByNameUsingGET(this.accountName?.value)
                .subscribe(
                  (account) => {
                    this.account = account;
                    if (['GITLAB', 'BITBUCKET'].includes(account?.provider)) {
                      this.getOrgsBasedOnAccountId(account);
                      this.getProjectKeyOptions(account?.['orgName']);
                    }
                    this.onAccountCreateSuccess(account);
                  },
                  (error) => {
                    if (error?.status === 404) {
                      this.onAccountPresent();
                    }
                  },
                );
            } else {
              const error = {
                error: {
                  message: response?.errorMessage,
                },
              };
              this.onAccountCreateFailure(error);
            }
            this.afterSaveAccount.emit(this.accountName?.value);
            // If response is received, stop polling
            this.stopPolling();
            triggerAnalyticsEvent(
              `ORG_SETTINGS_ACCOUNT_MANAGEMENT_ADDITION_SUCCESSFUL`,
              {
                accountName: this.accountName?.value,
                provider: this.vcsAccountType,
              },
            );
          }
        },
        (error) => {
          this.pollingWebhook = false;
          this.onAccountCreateFailure(error);
          triggerAnalyticsEvent(
            `ORG_SETTINGS_ACCOUNT_MANAGEMENT_ADDITION_ERROR`,
            {
              accountName: this.accountName?.value,
              provider: this.vcsAccountType,
            },
          );
        },
      );
  }

  async onAccountPresent() {
    this.alertType = 'warning';
    this.alertTitle = `GitHub app already installed`;
    this.alertDescription =
      'This organization already has an active GitHub integration. Please select another GitHub organization during installation to install it in another organization';
  }

  stopPolling() {
    if (this.webhookSubscription) {
      this.webhookSubscription.unsubscribe();
      this.pollingWebhook = false;
    }
  }

  getProjectKeyOptions(orgName?) {
    if (orgName) {
      this.projectKeyOptions = [];
      this.loadingProjectKey = true;
      this.accountControllerService
        .getBitbucketProjectsForWorkspaceUsingGET({
          workspace: orgName || this.account?.orgName,
          accountId: this.account?.id,
        })
        .subscribe(
          (projectKeys) => {
            projectKeys?.forEach((projectKey) => {
              this.projectKeyOptions.push({
                label: projectKey,
                value: projectKey,
              });
            });
            setTimeout(() => {
              this.projectId.setValue(this.account?.projectKey);
            }, 10);
            this.loadingProjectKey = false;
          },
          (err) => {
            this.projectKeyOptions = [];
            this.loadingProjectKey = false;
          },
        );
    }
  }

  updateVcsAccount() {
    this.updatingVcsAccount = true;
    const updateAccountParams = {
      id: this.account?.id,
      vcsAccountUpdateRequest: {
        organisationName: this.selectedOrgName?.value,
        projectKey: this.projectId?.value,
      },
    };
    this.accountControllerService
      .updateVCSAccountUsingPATCH(updateAccountParams)
      .subscribe(
        (response) => {
          this.customToast.success(
            `Your ${this.account?.name} account is updated successfully`,
          );
          this.afterSaveAccount.emit(this.account);
          this.passedRef?.close();
          this.updatingVcsAccount = false;
          this.orgsLinkedToAccount = true;
          triggerAnalyticsEvent(
            `ORG_SETTINGS_ACCOUNT_MANAGEMENT_ADDITION_LINK_ORG_SUCCESS`,
            {
              accountName: this.accountName?.value,
              provider: this.vcsAccountType,
            },
          );
          this.accountOrgUpdateError = false;
        },
        (error) => {
          this.updatingVcsAccount = false;
          this.orgsLinkedToAccount = false;
          this.accountOrgUpdateError = true;
          triggerAnalyticsEvent(
            `ORG_SETTINGS_ACCOUNT_MANAGEMENT_ADDITION_LINK_ORG_ERROR`,
            {
              accountName: this.accountName?.value,
              provider: this.vcsAccountType,
            },
          );
          this.customToast.error(
            error?.error?.message || `Failed to update account`,
          );
        },
      );
  }

  onClickHandleCancel(ref: any) {
    triggerAnalyticsEvent(`ORG_SETTINGS_ACCOUNT_MANAGEMENT_ADDITION_CANCEL`);
    ref.close();
  }

  async onAccountCreateSuccess(account) {
    this.accountCreateError = false;
    this.alertType = 'success';
    this.alertTitle = `Success! ${account?.name} connected to ${
      account?.provider === 'GITHUB' && account?.orgName
        ? account?.orgName + ' organization, on Facets'
        : 'Facets'
    }.`;
    this.alertDescription = `GitOps is enabled and your infrastructure configurations will now automatically sync between Facets & ${toTitleCase(
      account?.provider,
    )}`;
    this.afterSaveAccount.emit(account);
    await this.tokenExpiryService.extractExpiringAndExpiredVCSAccounts();
  }

  onAccountCreateFailure(error?) {
    this.resetInputFields();
    this.accountCreateError = true;
    this.enableStepTwo = false;
    this.customToast.error({
      title: 'Account linking failed',
      content:
        error?.error?.message ||
        'Please re-enter the correct details and try again.',
    });
  }

  getOrgsBasedOnAccountId(account) {
    this.loadingOrgNames = true;
    this.accountControllerService
      .getVcsOrganizationsByAccountIdUsingGET(account?.id)
      .subscribe(
        (response) => {
          this.loadingOrgNames = false;
          this.orgNames = response?.map((org) => ({ label: org, value: org }));
          this.selectedOrgName.setValue(account?.orgName);
        },
        () => {
          this.loadingOrgNames = false;
        },
      );
  }

  resetInputFields() {
    this.accountName.enable();
    this.hostedOn.enable();
    this.selectedOrgName.enable();
  }

  resetInputs() {
    this.resetInputFields();

    this.selectedOrgName.clearValidators();
    this.selectedOrgName.updateValueAndValidity();
    this.selectedOrgName.markAsUntouched();

    this.enableStepTwo = false;
    this.alertDescription = '';
    this.alertTitle = '';
    this.accountCreateError = false;
  }

  hostNameToggleEvent(event) {
    this.hostedOn?.setValue('github.com');
  }

  changeIntegrationMethod() {
    this.afterChangeIntegration.emit('PAT');
  }
}
